import {
  bundlePriceCentsOf,
  lunchClosedToday,
  lunchDateInBasket,
  lunchDateKey,
  lunchLineOf,
  lunchMealsIn,
  lunchRefusal,
  lunchSubtotalCents,
  lineKey,
  mainCount,
  useCart,
  type LunchBasketMeal,
} from '@agos/customer-core'
import {
  T,
  formatCents,
  lunchMealDescription,
  parseMoneyToCents,
  tt,
  useModalFocus,
  type LunchLinkedProduct,
  type LunchMeal,
  type LunchSettings,
} from '@agos/shared'
import { CategoryIcon, lunchSectionIconName } from '@agos/ui'
import { CalendarClock, Clock, Minus, Plus, Soup, UtensilsCrossed, X } from 'lucide-react'
import { useRef, useState } from 'react'

import { lunchSections, type LunchDayOption } from './lunch-days'

/**
 * The daily menu, in the sheet legacy opens from the category bar.
 *
 * A day per tab, the sections the franchise configured, and a stepper per meal. The **soup bundle
 * price** is the one thing here that is not a plain list: a soup is cheaper alongside a main, and how
 * many of a customer's soups get that price depends on how many mains are in the basket. The rule is in
 * `@agos/customer-core` and derived on every render, so the price on the screen and the price on the
 * order cannot come apart — legacy keeps two cart lines per soup in step by hand and has four functions
 * to do it.
 *
 * **Ordering a day is all-or-nothing.** A basket may carry one day's lunch, because the restaurant
 * cooks the day's menu on that day. The refusal says which day is already in it rather than just
 * refusing.
 */
export function LunchMenuModal({
  days,
  settings,
  now,
  onClose,
  categories = [],
  products = [],
}: {
  days: readonly LunchDayOption[]
  settings: LunchSettings
  now: Date
  onClose: () => void
  /** §9.91 — so a section can draw the icon its category was given. */
  categories?: readonly { name?: string | null; icon?: string | null }[]
  /** §9.401 - a meal's popis is its linked product's, or there is none. */
  products?: readonly LunchLinkedProduct[]
}) {
  const [selected, setSelected] = useState(0)
  const lines = useCart((state) => state.lines)
  const add = useCart((state) => state.add)
  const changeBy = useCart((state) => state.changeBy)

  /* §9.489, `TODO.md` 3f - it said `aria-modal="true"` and confined nothing, on a customer's own keyboard.
     **The role is on the card, not the wrapper**: the wrapper is the dismiss scrim and says `role="presentation"`
     so it is not itself announced, which is §9.469's rule arrived at from this markup. The close is
     unconditional because the scrim already calls it on a tap with no guard, and this overlay holds no write.

     **Above `if (day === undefined) return null`, which lint named.** A hook below an early return is called on
     some renders and not others; the reflex is to put it beside the markup it serves, and that is where this
     file's early return happens to be. */
  const panelRef = useRef<HTMLDivElement>(null)
  const { onKeyDown } = useModalFocus(true, panelRef, onClose)


  const day = days[selected] ?? days[0]
  if (day === undefined) return null

  // §9.710 - this tab's own lines: next Tuesday's soup is not today's, though it is the same meal.
  const basket = lunchMealsIn(lines, day.meals, day.key)
  const todayKey = lunchDateKey(now)
  const closedToday = lunchClosedToday(settings.timeTo, now)
  const isToday = day.key === todayKey

  // Which day the basket already holds, across every day on offer — not just the one being shown.
  // §9.710, FB-1014 - the day a line was picked for, where it says. Matching meals against the tabs found the
  // *first* tab carrying the meal, so next Tuesday's lunch read as today's and next Tuesday's own tab was refused.
  const basketDay =
    lunchDateInBasket(lines, days.flatMap((option) => option.meals)) ??
    days.find((option) => lunchMealsIn(lines, option.meals).length > 0)?.key ??
    null

  const refusal = lunchRefusal({
    visible: settings.visible,
    closedToday,
    isToday,
    basketDay,
    day: day.key,
  })

  const sections = lunchSections(settings, day.meals)
  const mains = mainCount(basket)
  const subtotal = lunchSubtotalCents(basket, settings.soupBundlePrice ?? null)
  const bundleFallback = settings.soupBundlePrice ?? null

  function quantityOf(meal: LunchMeal): number {
    return basket.find((entry) => entry.meal.id === meal.id)?.quantity ?? 0
  }

  return (
    <div
      className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
      data-lunch-modal
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={tt('Obedové menu')}
      ref={panelRef}
      onKeyDown={onKeyDown}
        onClick={(event) => event.stopPropagation()}
        className="w-full sm:max-w-2xl bg-background rounded-t-3xl sm:rounded-3xl shadow-2xl border border-border max-h-[85vh] flex flex-col overflow-hidden"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Soup className="w-5 h-5 text-amber-500" />
            <T>Obedové menu</T>
          </h2>
          <button
            type="button"
            onClick={onClose}
            data-lunch-close
            aria-label={tt('Zavrieť')}
            data-dialog-cancel
            className="p-2.5 rounded-full hover:bg-secondary transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {days.length > 1 && (
          <div
            className="flex flex-nowrap gap-1.5 px-3 sm:px-4 py-2 overflow-x-auto border-b border-border bg-background shrink-0 hide-scrollbar"
            data-lunch-days
          >
            {days.map((option, index) => (
              <button
                key={option.key}
                type="button"
                data-lunch-day={option.key}
                aria-pressed={index === selected}
                onClick={() => setSelected(index)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-colors border shrink-0 ${
                  index === selected
                    ? 'bg-amber-500 text-white border-amber-500'
                    : 'bg-muted text-muted-foreground border-border hover:text-foreground'
                }`}
              >
                {option.dayName} {option.shortDate}
              </button>
            ))}
          </div>
        )}

        <div className="px-3 sm:px-5 py-4 space-y-5 overflow-y-auto flex-1">
          {refusal !== '' && (
            <div
              className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-600 text-xs font-semibold"
              data-lunch-refusal
            >
              <Clock className="w-4 h-4 shrink-0" />
              <span>{refusal}</span>
            </div>
          )}

          {/* Legacy's own note, and it is a real constraint rather than a nicety: a preorder for a
              future day is delivered inside the lunch window, not whenever the customer asks. */}
          {refusal === '' && !isToday && settings.timeFrom !== '' && settings.timeTo !== '' && (
            <div
              className="flex items-start gap-2.5 px-3 py-2.5 rounded-xl bg-primary/5 border border-primary/30 text-primary text-xs font-semibold"
              data-lunch-preorder-note
            >
              <CalendarClock className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                <T>Obedové menu na iný deň sa doručuje v obedových hodinách</T> ({settings.timeFrom} –{' '}
                {settings.timeTo}).
              </span>
            </div>
          )}

          {/* §9.17: the window is five days whether or not each has a menu, so a day can be empty.
              Saying so is the point - a tab that opened onto nothing, or vanished, would be worse. */}
          {day.meals.length === 0 && (
            <p className="text-sm text-muted-foreground py-6 text-center" data-lunch-day-empty>
              <T>Na tento deň nie je obedové menu.</T>
            </p>
          )}

          {sections.map((section) => {
            const meals = day.meals.filter((meal) => meal.type === section.type)
            const isSoup = section.type === 'soup'
            return (
              <div key={section.type} data-lunch-section={section.type}>
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  {/* §9.91 - the category's icon, where the franchise has chosen one. Each section
                    * drew either a bowl of soup or the crossed forks before this. */}
                  <CategoryIcon
                    icon={lunchSectionIconName(section.label, categories)}
                    className="w-3.5 h-3.5"
                  />
                  {section.label}
                </p>
                <div className="flex flex-col gap-1.5">
                  {meals.map((meal) => {
                    const quantity = quantityOf(meal)
                    const own = parseMoneyToCents(meal.price)
                    const bundle = isSoup ? bundlePriceCentsOf(meal, bundleFallback) : null
                    // **The headline is what the next one added will cost** — FB-C22. A bundle price is
                    // only earned once there is a main to earn it against, and legacy shows the 0,50 €
                    // as the big number regardless, with *bez menu 1,50 €* underneath. A customer who
                    // adds a soup on its own is then charged three times what the price said.
                    const cheaper = bundle !== null && bundle < own
                    const earned = cheaper && mains > basket
                      .filter((entry) => entry.meal.type === 'soup')
                      .reduce((count, entry) => count + entry.quantity, 0)
                    return (
                      <div
                        key={meal.id}
                        data-lunch-meal={meal.id}
                        className={`rounded-2xl border transition-all overflow-hidden ${
                          quantity > 0
                            ? isSoup
                              ? 'bg-amber-500/8 border-amber-500/40'
                              : 'bg-primary/8 border-primary/30'
                            : 'bg-card border-border'
                        }`}
                      >
                        <div className="flex items-start gap-2.5 px-3 pt-3 pb-1">
                          {isSoup ? (
                            <Soup className="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />
                          ) : (
                            <UtensilsCrossed className="w-4 h-4 shrink-0 mt-0.5 text-primary" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-foreground text-sm leading-tight">{meal.name}</p>
                            {/* §9.401 - the linked product's popis, never the stored copy. The
                              * owner's *Palacinky Džemové* was describing a *Parená buchta*,
                              * because legacy's picker overwrites the name and leaves the
                              * sentence. */}
                            {lunchMealDescription(meal, products) !== '' && (
                              <p
                                className="text-xs text-gray-500 mt-0.5 leading-snug italic"
                                data-lunch-meal-descr
                              >
                                {lunchMealDescription(meal, products)}
                              </p>
                            )}
                            {meal.allergens.length > 0 && (
                              <p className="text-xs text-muted-foreground mt-0.5">
                                <T>al.</T> {meal.allergens.join(', ')}
                              </p>
                            )}
                          </div>
                          <div className="text-right shrink-0">
                            {earned && bundle !== null ? (
                              <>
                                <div className="flex items-baseline gap-1.5">
                                  <span className="font-extrabold text-amber-500 text-base" data-lunch-price>
                                    {formatCents(bundle)}
                                  </span>
                                  <span className="text-xs text-muted-foreground line-through">
                                    {formatCents(own)}
                                  </span>
                                </div>
                                <p className="text-[10px] text-amber-500/70">
                                  <T>s menu</T>
                                </p>
                              </>
                            ) : (
                              <>
                                {/* §9.57 - the menu price leads, the standalone one is stated under
                                  * it. This was the other way round (FB-C22: the headline is what
                                  * the next one costs), and the owner asked for legacy's order with
                                  * both figures kept: *nech to človek vie*. */}
                                <span
                                  className={`font-extrabold text-base ${cheaper && bundle !== null ? 'text-amber-500' : 'text-foreground'}`}
                                  data-lunch-price
                                >
                                  {formatCents(cheaper && bundle !== null ? bundle : own)}
                                </span>
                                {cheaper && bundle !== null && (
                                  <p className="text-[10px] text-muted-foreground" data-lunch-bundle-price>
                                    <T>bez menu</T> {formatCents(own)}
                                  </p>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-end gap-2 px-3 pb-3 pt-1">
                          {quantity > 0 && (
                            <>
                              <button
                                type="button"
                                data-lunch-remove={meal.id}
                                aria-label={tt('Menej')}
                                onClick={() => changeBy(keyOf(meal, basket.find((entry) => entry.meal.id === meal.id)?.date), -1)}
                                className="w-11 h-11 rounded-full bg-secondary text-foreground flex items-center justify-center active:scale-95 transition-colors"
                              >
                                <Minus className="w-4 h-4" />
                              </button>
                              <span className="font-bold text-foreground min-w-[1.5rem] text-center">
                                {quantity}
                              </span>
                            </>
                          )}
                          <button
                            type="button"
                            data-lunch-add={meal.id}
                            aria-label={tt('Do košíka')}
                            disabled={refusal !== ''}
                            onClick={() => add({ ...lunchLineOf(meal, day.key), quantity: 1 })}
                            className="w-11 h-11 rounded-full bg-green-600 text-white flex items-center justify-center hover:bg-green-700 active:scale-95 transition-colors disabled:opacity-50"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>

        {basket.length > 0 && (
          <div className="border-t border-border px-5 py-4 flex items-center justify-between shrink-0">
            <span className="text-sm font-bold" data-lunch-subtotal>
              <T>Obedové menu</T>: {formatCents(subtotal)}
            </span>
            <button
              type="button"
              data-lunch-done
              onClick={onClose}
              className="min-h-[44px] px-5 rounded-2xl bg-green-600 hover:bg-green-700 text-white text-sm font-bold transition-colors"
            >
              <T>Hotovo</T>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

/** The cart's key for a lunch line — `lineKey`'s, so a lunch line is keyed like any other. */
function keyOf(meal: LunchMeal, date: string | undefined): string {
  // §9.710 - the line's own day, which is how it was keyed; an undated line saved before that keeps the old key.
  return lineKey(lunchLineOf(meal, date))
}

export type { LunchBasketMeal }
